import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ToastContainer } from 'react-toastify';
import axios from 'axios';
import cookie from 'react-cookies';
import Swal from 'sweetalert2';
import RingLoader from 'react-spinners/RingLoader';
import Helmet from 'react-helmet';
import { permissionTypes } from 'drapcode-constant';
import 'react-toastify/dist/ReactToastify.css';
import 'react-dropzone-uploader/dist/styles.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-intl-tel-input/dist/main.css';
import * as actions from '../../features/home/redux/actions';
import * as commonActions from '../../features/common/redux/actions';
import DefaultPage from '../tutorial/DefaultPage';
import TopNav from './TopNav';
import SideBar from './sidebar/SideBar';
import { WorkspaceChromeProvider } from './shell/WorkspaceChromeContext';
import ItemDetail from '../collections/item/ItemDetail';
import '../../webconnect';
import { Mixpanel } from '../../common/utils/mixpanelUtil';
import { updateUserIpInCookie } from '../../common/utils/api_utils';
import { resetCookie, saveCookie, saveOrganizationCookie } from '../../common/utils/check-auth';
import Shortcut from '../../shortcut/Shortcut';

const css = {
  margin: 'auto',
  marginTop: '15%',
  display: 'block',
};

const toastrStyle = {
  width: 'auto',
  minWidth: '15vw',
};

const alertOptions = {
  allowOutsideClick: false,
  allowEscapeKey: false,
  allowEnterKey: false,
  icon: 'error',
  title: 'Oops...',
};

const LAYOUT_PREFIX = '/2.0';

function parseLayoutPath(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== '2.0') {
    return { version: undefined, projectId: undefined };
  }
  return {
    version: parts[2],
    projectId: parts[4],
  };
}

function isBareLayoutPath(pathname) {
  return (
    pathname === '/login' ||
    pathname === '/' ||
    pathname === LAYOUT_PREFIX ||
    pathname === `${LAYOUT_PREFIX}/`
  );
}

export class App extends Component {
  static propTypes = {
    children: PropTypes.node,
    home: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
  };

  state = {
    showLoader: false,
    showContent: false,
  };

  static defaultProps = {
    children: '',
  };

  async componentDidMount() {
    const { actions } = this.props;
    const { version, projectId } = parseLayoutPath(this.props.location.pathname);

    const orgSurfaceUrl = process.env.REACT_APP_ORGANIZATION;
    const orgEngineUrl = process.env.REACT_APP_ORGANIZATION_ENGINE;
    if (!projectId) {
      window.location.href = `${orgSurfaceUrl}`;
    }
    this.setState({ showLoader: true });
    if (process.env.REACT_APP_ENVIRONMENT === 'DEV') {
      this.setState({ showLoader: false, showContent: true });
      await actions.loadProjectDetail({ projectId, version });
      await actions.loadInstalledPlugin({ projectId, version });
      await actions.loadProjectVersion({ projectId });
      await actions.loadCollectionChange();
      saveOrganizationCookie(permissionTypes, 'UNLIMITED');
    } else {
      let config = {
        headers: {
          Authorization: `Bearer ${cookie.load('token')}`,
        },
      };
      const clientDetailUrl = `${orgEngineUrl}clients/details/${projectId}/project`;
      axios
        .get(clientDetailUrl, config)
        .then(async (response) => {
          let { status, message, user, organization, role } = response.data;
          if (status === 'FAILED') {
            resetCookie();
            Swal.fire({
              ...alertOptions,
              text: message,
              confirmButtonText: 'Try Again',
              footer: `<a href="${orgSurfaceUrl}contact-us">Why do I have this issue?</a>`,
            }).then((result) => {
              if (result.isConfirmed) {
                window.location.href = `${orgSurfaceUrl}`;
              }
            });
            this.setState({ showLoader: false });
            return;
          } else if (status === 'SUCCESS') {
            await actions.loadProjectDetail({ projectId, version });
            await actions.loadInstalledPlugin({ projectId, version });
            await actions.loadProjectVersion({ projectId });
            await actions.loadCollectionChange();
            if (role === 'ROLE_ADMIN') {
              organization = { permissions: permissionTypes, subscription: 'UNLIMITED' };
            }
            saveCookie(user, organization);
            this.setState({ showLoader: false, showContent: true });
          } else {
            window.location.href = orgSurfaceUrl;
            resetCookie();
            this.setState({ showLoader: false });
            return;
          }
        })
        .catch((err) => {
          console.log('err :>> ', err);
          resetCookie();
          this.setState({ showLoader: false });
          if (err && err.response && err.response.status === 401) {
            Swal.fire({
              ...alertOptions,
              text: 'Your have been logged out due to inactivity. Please login again.',
              confirmButtonText: 'Login',
            }).then((result) => {
              if (result.isConfirmed) {
                window.location.href = `${orgSurfaceUrl}`;
              }
            });
          }
          window.location.href = `${orgSurfaceUrl}`;
          return;
        });
    }

    const userIp = cookie.load('userIp');
    if (!userIp) {
      await updateUserIpInCookie();
    }
    const lastAccess = cookie.load('lastAccess');
    if (!lastAccess) {
      await actions.addLastAccess({ projectId });
    }
  }

  showTopNav = () => {
    const { location, common, actions } = this.props;
    const { projectDetail, versions } = common;
    const { version, projectId } = parseLayoutPath(location.pathname);

    if (projectId) {
      return (
        <Fragment>
          <TopNav
            projectId={projectId}
            actions={actions}
            projectDetail={projectDetail}
            version={version}
            versions={versions}
            location={location}
          />
        </Fragment>
      );
    }
  };

  showBuilder = () => {
    const { location } = this.props;
    const { version, projectId } = parseLayoutPath(location.pathname);

    if (projectId) {
      return (
        <Fragment>
          <Shortcut projectId={projectId} version={version} />
          <SideBar projectId={projectId} version={version} />
          <DefaultPage />
          <ItemDetail projectId={projectId} version={version} />
        </Fragment>
      );
    }
  };

  render() {
    const { location, children, common } = this.props;
    const path = location.pathname;
    const { showLoader, showContent } = this.state;
    const { projectDetail } = common;
    if (showLoader) {
      return <RingLoader css={css} size="80px" color={'#1d1d1d'} loading={showLoader} />;
    }

    if (isBareLayoutPath(path)) {
      return <div className="layout-container">{children}</div>;
    } else if (projectDetail && projectDetail.isDeleted) {
      return (
        <div className="page-container bg-blue-grey-900 pt-2">
          <h1 style={{ textAlign: 'center' }}>This project has been deleted.</h1>
        </div>
      );
    } else {
      return (
        <WorkspaceChromeProvider>
          <Helmet>
            <title>DrapCode | Build Awesome Web Applications</title>
          </Helmet>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            style={toastrStyle}
          />
          {this.showTopNav()}
          {this.showBuilder()}
          {children}
        </WorkspaceChromeProvider>
      );
    }
  }
}

function mapStateToProps(state) {
  return {
    home: state.home,
    common: state.common,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions, ...commonActions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
